/* @refresh reset */
/**
 * MODULE: _shared/frontend-shell/src/components/notifications/NotificationBell.tsx
 * PURPOSE: Header bell — G26 data-management inbox, unread count (respects mutes/snooze), /activities link
 * DEPENDENCIES: notificationApi, useApiAuthReady, lucide-react Bell, date-fns, react-router-dom, @tanstack/react-query
 * AGENT SAFETY: SAFE
 * FILE_SIZE: 206 / 220 lines
 * RECENT: 2026-06-04 — **`useApiAuthReady`** gates queries; **`notificationApi`** handles **401** refresh+retry. [2.2.795] **`getAuthToken()`** gate. [2.2.562] TanStack **`retry: false`** …
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  notificationApi,
  type TenantNotification,
  type NotificationCategory,
} from '../../../../lib/frontend/api/notificationApi';
import { useApiAuthReady } from '../../hooks/useApiAuthReady';

function relativeWhen(iso?: string): string {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

/** Limits refetch storms when notification-service returns 5xx or missing routes (404). */
const NOTIFICATION_QUERY_OPTS = {
  retry: false as const,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30_000),
  refetchOnWindowFocus: false as const,
};

export function NotificationBell() {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const notificationsReady = useApiAuthReady();

  const { data: prefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      try {
        return await notificationApi.getPreferences();
      } catch {
        return { muted_categories: [] as NotificationCategory[] };
      }
    },
    staleTime: 120_000,
    enabled: notificationsReady,
    ...NOTIFICATION_QUERY_OPTS,
  });
  const muted = new Set<NotificationCategory>(prefs?.muted_categories ?? []);

  const { data: items = [], refetch: refetchList } = useQuery({
    queryKey: ['notification-bell-list'],
    queryFn: async () => {
      try {
        return await notificationApi.list({ limit: 50, include_snoozed: false });
      } catch {
        return [];
      }
    },
    staleTime: 90_000,
    enabled: notificationsReady,
    ...NOTIFICATION_QUERY_OPTS,
  });

  const { data: unreadCount = 0, refetch: refetchCount } = useQuery({
    queryKey: ['notification-bell-unread'],
    queryFn: async () => {
      try {
        return await notificationApi.unreadCount();
      } catch {
        return 0;
      }
    },
    staleTime: 90_000,
    enabled: notificationsReady,
    ...NOTIFICATION_QUERY_OPTS,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['notification-bell-list'] });
    await queryClient.invalidateQueries({ queryKey: ['notification-bell-unread'] });
    await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
  }, [queryClient]);

  useEffect(() => {
    if (!notificationsReady) return;
    const t = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(t);
  }, [refresh, notificationsReady]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const visibleItems = items.filter((n) => !muted.has((n.category || 'system') as NotificationCategory));
  const preview = visibleItems.slice(0, 6);

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  const onRowClick = async (n: TenantNotification) => {
    if (!n.is_read) {
      try {
        await notificationApi.markRead(n.id);
        await refresh();
      } catch {
        /* ignore */
      }
    }
    if (n.action_url?.startsWith('/')) {
      navigate(n.action_url);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="notification-bell"
        className="relative rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
        aria-label="Notifications"
        onClick={() => {
          setOpen((v) => !v);
          // Manual refetch bypasses **`enabled`**; skip when there is no Bearer for **`getAuthHeaders`**.
          if (notificationsReady) {
            void refetchList();
            void refetchCount();
          }
        }}
      >
        <Bell className="h-6 w-6" strokeWidth={1.75} />
        {unreadCount > 0 ? (
          <span
            data-testid="notification-badge"
            className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
          >
            {badgeLabel}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          <div className="border-b border-gray-100 px-3 pb-2 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
            Notifications
          </div>
          <div className="max-h-72 overflow-y-auto">
            {preview.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No notifications</p>
            ) : (
              preview.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  data-testid="notification-item"
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/80"
                  onClick={() => void onRowClick(n)}
                >
                  <span className="font-medium text-gray-900 dark:text-gray-100">{n.title}</span>
                  {n.message ? (
                    <span className="line-clamp-2 text-xs text-gray-600 dark:text-gray-300">{n.message}</span>
                  ) : null}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {n.category ? `${n.category} · ` : ''}
                    {relativeWhen(n.created_at)}
                  </span>
                </button>
              ))
            )}
          </div>
          <div className="border-t border-gray-100 px-3 pt-2 dark:border-gray-700">
            {items.length > 0 ? (
              <button
                type="button"
                className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                onClick={async () => {
                  try {
                    await notificationApi.markAllRead();
                    await refresh();
                  } catch {
                    /* ignore */
                  }
                }}
              >
                Mark all read
              </button>
            ) : null}
            <Link
              to="/activities"
              data-testid="notification-view-all"
              className="mt-1 block text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
              onClick={() => setOpen(false)}
            >
              View all
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
